import type { ESubjects, TPermissionAction } from "@/api/models/auth/ability";
import type { TComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import type { NestedKeyOf, OneOrManyOrNull } from "@/types/general";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { Ref } from "vue";
import { getHttpErrorStatus } from "@/router/queryErrorRedirect";
import type { TTreatmentPlanJobSignal } from "@/api/models/treatmentPlan/job/types";

export const DEFAULT_STALE_TIME = 15 * 1000;

/**
 * Default no-retry codes for item queries (`useGenericQuery`).
 *
 * 429 is included deliberately: the API throttles by IP with a multi-second window, so an
 * immediate retry cannot succeed — it only burns the remaining budget and doubles the
 * request count (which also trips the e2e duplicate-request guard).
 */
export const DEFAULT_ITEM_NO_RETRY_STATUS_CODES = new Set([400, 403, 404, 429]);

/** Default no-retry codes for list / detail / paginated queries. */
export const DEFAULT_LIST_NO_RETRY_STATUS_CODES = new Set([403, 404, 429]);

/**
 * Merges default no-retry status codes with any caller-provided extras.
 * Caller codes are additive — they never replace the defaults.
 */
export function mergeNoRetryStatusCodes(
  defaults: ReadonlySet<number>,
  incoming?: Iterable<number>
): Set<number> {
  return new Set([...defaults, ...(incoming ?? [])]);
}

/**
 * Which query failures to keep out of the snackbar: all of them, the listed HTTP status codes,
 * or whatever a predicate matches (for failures identified by an axios `code` or a payload
 * error code rather than a status).
 */
export type TSuppressErrors = boolean | number[] | ((error: unknown) => boolean);

/**
 * Whether a failed query should stay silent, given its `suppressErrors` setting.
 *
 * Anything not matched still reports normally — so a caller silencing one expected response
 * (a 404 "nothing yet", a 412 "not configured") keeps real failures like 403 visible.
 */
export function shouldSuppressQueryError(
  suppressErrors: TSuppressErrors | undefined,
  error: unknown
): boolean {
  if (!suppressErrors) return false;
  if (suppressErrors === true) return true;
  if (typeof suppressErrors === "function") return suppressErrors(error);
  const status = getHttpErrorStatus(error);
  return status !== undefined && suppressErrors.includes(status);
}

/**
 * Normalizes a `MaybeRefOrGetter<T>` (plain value, ref, or getter) into a reactive `Ref<T>`.
 * Uses `computed` so that refs and computed refs are properly unwrapped rather than
 * double-wrapped (which `toRef` can do when passed an existing ref).
 */
export function useFiltersRef<T>(source: MaybeRefOrGetter<T> | undefined): Ref<T | undefined> {
  return computed(() => toValue(source)) as Ref<T | undefined>;
}

/**
 * Generic params type for query composables.
 * Extends `TComposableParams` (enabled, lazy, usePublic) with query-specific extras
 * such as `filters` or `id`.
 *
 * @template TExtra - Additional properties specific to a given composable (e.g. `{ filters?: ... }`).
 */
export type TQueryParams<TExtra extends object = Record<never, never>> = TComposableParams & TExtra;

/**
 * Freshness / refetch tuning knobs common to every query composable.
 *
 * Kept as a standalone type (not just inlined into `TBaseQueryOptions`) so a composable
 * can expose exactly these caller-facing options — e.g. `usePatient(params, suppress, behavior)` —
 * without also surfacing the internal query wiring (`queryKey`, `fetcher`, `params`, ...).
 */
export type TQueryBehaviorOptions = {
  /**
   * When true, redirects to the matching router error page on object fetch failure:
   * HTTP 404 → 404 page, HTTP 403 → 401 page (same as a failed CASL route check).
   * Implicitly suppresses the error snackbar.
   */
  redirectOnNotFound?: MaybeRefOrGetter<boolean>;
  /**
   * Time in milliseconds that fetched data is considered fresh.
   * While fresh, the query will not refetch on mount or window focus.
   * Defaults to TanStack Query's own default (0) if omitted.
   */
  staleTime?: number;
  /**
   * Whether to refetch the query when the window regains focus.
   * Defaults to TanStack Query's own default (true) if omitted.
   */
  refetchOnWindowFocus?: boolean;
  /**
   * Whether to refetch the query when the network connection is restored.
   * Defaults to TanStack Query's own default (true) if omitted.
   */
  refetchOnReconnect?: boolean;
};

/**
 * Error-handling options pass-through for item-detail query composables.
 *
 * @template TResponseItem - Fetched item type, used to type `accessCheck`.
 */
export type TItemQueryBehaviorOptions<TResponseItem = unknown> = Pick<
  TBaseQueryOptions,
  | "suppressErrors"
  | "redirectOnNotFound"
  | "noRetryStatusCodes"
  | "staleTime"
  | "refetchOnWindowFocus"
  | "refetchOnReconnect"
> & {
  /**
   * When provided, runs against successful fetch results. Returning `false`
   * redirects to the unauthorized page (same as HTTP 403).
   */
  accessCheck?: (data: TResponseItem) => boolean;
};

/**
 * Base options shared by all generic query composables.
 *
 * @template TParamsExtra - Additional properties merged into `params` beyond `TComposableParams`.
 *   Defaults to `Record<never, never>` (no extras), giving a plain `TComposableParams` params shape.
 *   Pass e.g. `{ filters?: MaybeRefOrGetter<TFilters> }` to extend it.
 */
export type TBaseQueryOptions<TParamsExtra extends object = Record<never, never>> =
  TQueryBehaviorOptions & {
    /**
     * The base query key to use for TanStack Query.
     */
    queryKey: MaybeRefOrGetter<readonly any[]>;
    /**
     * An optional CASL permission subject(s). If provided, the query is only enabled
     * when `can(action, subject)` is true (see `action`; defaults to READ).
     */
    permission?: MaybeRefOrGetter<OneOrManyOrNull<ESubjects>>;
    /**
     * Optional CASL action, used with `permission` in `can(action, subject)`.
     * Defaults to READ when `permission` is set.
     */
    action?: MaybeRefOrGetter<TPermissionAction>;
    /**
     * Extra HTTP status codes that should not trigger a retry upon query failure.
     * Merged with the composable's defaults (additive — does not replace them).
     */
    noRetryStatusCodes?: number[];
    /**
     * Suppresses the error snackbar on query failure.
     *
     * `true` suppresses every failure. A list of HTTP status codes, or a predicate for
     * failures identified by something other than status (an axios `code`, a payload error
     * code), suppresses only those and lets everything else — 403 included — reach the
     * snackbar as usual. Defaults to false.
     */
    suppressErrors?: TSuppressErrors;
    /**
     * General composable parameters, including `enabled`, `lazy`, `usePublic`,
     * and any composable-specific extras (e.g. `filters`, `id`).
     */
    params?: TQueryParams<TParamsExtra>;
    /**
     * Keep the query enabled while pending terms block the app (every other query is
     * force-disabled). Reserved for the terms documents themselves — the user must be able to
     * read what the acceptance dialog is asking them to accept.
     */
    ignorePendingTermsBlock?: boolean;
    /**
     * Mark the query as authorized by a treatment-plan job, and name which job it needs.
     *
     * Endpoints under such a resource answer 403 without the job, so declaring this holds the
     * query until the job resolves, sends it as `job_id`, and keys the cache on it. The signal
     * names *which* job — the store is the authoritative source. Passing `true` instead falls
     * back to the `job_id` in the URL, which cannot distinguish job types: only use it where no
     * signal is known.
     */
    jobSignal?: MaybeRefOrGetter<TTreatmentPlanJobSignal | true | undefined>;
  };

/**
 * Intermediate base for filter-driven query composables (list, detail, paginated).
 * Extends `TBaseQueryOptions` with `neededKey` — keys within `TFilters` that must be
 * defined for the query to be enabled.
 *
 * @template TFilters - The filters type. Paginated callers pass `Partial<TFilters>` here
 *   to align params with their `Partial<TFilters>` filters shape.
 */
export type TBaseFilterQueryOptions<TFilters = unknown> = TBaseQueryOptions<{
  filters?: MaybeRefOrGetter<TFilters>;
}> & {
  /**
   * An array of property paths (e.g. `nested.key`) within the `filters` object that *must*
   * be defined and not `undefined` for the query to be enabled.
   */
  neededKey?: NestedKeyOf<TFilters>[];
};
